import type { Language } from '@offer-catalog/services/i18n'
import type { TFunction } from 'i18next'
import type { FilterGroup } from './ProductsContext'
import type { Brand, DdfsProductNature as ProductNature, Sport, SportGroup } from '@/api/ddfs/ddfs'
import type { FacetBucket, FacetsResponse, LabeledFacetBucket } from '@/types/catalog'
import type { CountryItem } from '@/types/countries'
import { fetchAllCountries, fetchCountriesByCodes } from '@/api/ddfs/countries'
import { fetchDdfsCollection } from '@/api/ddfs/ddfs'
import { HOMOLOGATION_STATUS_CONFIG } from '@/constants/homologation'

export { facetParameters, normalizeFacetFilters } from './facetFilters'
export { getFacetFiltersFromSearchParams } from './facetFilters'

const facetGroupIds: (keyof FacetsResponse)[] = [
  'subdepartments',
  'families',
  'product_natures',
  'brands',
  'sports',
  'sports_groups',
  'dmi_segmentations',
  'genders',
  'product_statuses',
  'ecodesign_statuses',
  'design_centers',
  'selected_customs_zones',
  'production_countries',
  'homologation_statuses',
  'validation_statuses',
]

const ecoDesignStatusTranslationKeys: Record<string, string> = {
  TRUE: 'true',
  FALSE: 'false',
  UNDEFINED: 'undefined',
}

const INITIAL_LABEL_LIMIT = 10

export type FacetLabels = Partial<Record<string, Partial<Record<string, Partial<Record<Language, string>>>>>>

interface DesignCenter {
  designation: string
}

function fallbackLabels(key: string): Record<Language, string> {
  return { en: key, fr: key }
}

function getLocalizedLabels(
  translationPath: string,
  key: string,
  t: TFunction,
  fallback?: Record<Language, string>,
): Record<Language, string> {
  const translate = (language: Language) => fallback
    ? t(`${translationPath}.${key}`, { defaultValue: fallback[language], lng: language })
    : t(`${translationPath}.${key}`, { lng: language })

  return {
    en: translate('en'),
    fr: translate('fr'),
  }
}

function getGenderLabels(key: string, t: TFunction): Record<Language, string> {
  const translationPath = 'products-page.product-card.gender-id'
  const fallback = getLocalizedLabels(translationPath, 'unknown', t)

  return getLocalizedLabels(translationPath, key, t, fallback)
}

function getEcoDesignStatusLabels(key: string, t: TFunction): Record<Language, string> {
  const translationKey = ecoDesignStatusTranslationKeys[key]

  if (!translationKey)
    return fallbackLabels(key)

  return getLocalizedLabels('products-page.product-card.ecodesign', translationKey, t)
}

function getHomologationStatusLabels(key: string, t: TFunction): Record<Language, string> {
  const translationKey = HOMOLOGATION_STATUS_CONFIG.find(status => status.facetStatus === key)?.translationKey

  if (!translationKey)
    return fallbackLabels(key)

  const translationPath = 'products-page.product-card.homologation_statuses'

  return getLocalizedLabels(translationPath, translationKey, t)
}

function getValidationStatusLabels(key: string, t: TFunction): Record<Language, string> {
  const translationPath = 'products-page.filters.validation-statuses'
  const fallback = getLocalizedLabels(translationPath, 'unknown', t)

  return getLocalizedLabels(translationPath, key, t, fallback)
}

function getDesignCenterLabel(
  id: string,
  language: Language,
  czListSetsByCustomZone: Record<string, DesignCenter>,
  t: TFunction,
): string {
  const designation = czListSetsByCustomZone[id]?.designation

  if (designation?.trim())
    return designation.replace(/^CZ\s+/, '')

  return t('products-page.filters.unknown-design-center', { id, lng: language })
}

function getCustomsZoneLabel(
  id: string,
  language: Language,
  czListSetsByCustomZone: Record<string, DesignCenter>,
  t: TFunction,
): string {
  const designation = czListSetsByCustomZone[id]?.designation

  if (designation?.trim())
    return designation.replace(/^CZ\s+/, '')

  return t('products-page.product-card.unknown-customs-zone', { id, lng: language })
}

function getFacetLabel(
  groupId: string,
  key: string,
  t: TFunction,
  labels: FacetLabels,
): Record<Language, string> {
  switch (groupId) {
    case 'dmi_segmentations': {
      const label = key.replace('_', ' ')

      return { en: label, fr: label }
    }
    case 'genders':
      return getGenderLabels(key, t)
    case 'ecodesign_statuses':
      return getEcoDesignStatusLabels(key, t)
    case 'homologation_statuses':
      return getHomologationStatusLabels(key, t)
    case 'validation_statuses':
      return getValidationStatusLabels(key, t)
    default:
      return { ...fallbackLabels(key), ...labels[groupId]?.[key] }
  }
}

function hasFacetLabels(bucket: FacetBucket<string | number>): bucket is LabeledFacetBucket {
  if (!('labels' in bucket) || typeof bucket.labels !== 'object' || bucket.labels === null)
    return false

  return 'en' in bucket.labels
    && typeof bucket.labels.en === 'string'
    && 'fr' in bucket.labels
    && typeof bucket.labels.fr === 'string'
}

export function mapFacetsToFilterGroups(
  t: TFunction,
  response: Partial<FacetsResponse> = {},
  labels: FacetLabels = {},
): FilterGroup[] {
  return facetGroupIds.map(id => ({
    id,
    filters: (response[id] ?? []).map((bucket) => {
      const key = String(bucket.key)
      return {
        id: key,
        label: hasFacetLabels(bucket) ? bucket.labels : getFacetLabel(id, key, t, labels),
        count: bucket.count,
      }
    }),
  }))
}

function getFacetIds<T extends string | number>(buckets: FacetBucket<T>[] | undefined): string[] {
  return [...new Set((buckets ?? []).map(bucket => String(bucket.key)))]
}

function getInitialFacetIds<T extends string | number>(buckets: FacetBucket<T>[] | undefined): string[] {
  return [...new Set((buckets ?? []).map(bucket => String(bucket.key)))].slice(0, INITIAL_LABEL_LIMIT)
}

function toLabelMap(items: readonly unknown[], language: Language): Record<string, Partial<Record<Language, string>>> {
  return Object.fromEntries(
    items
      .filter((item): item is { id: number, name: string } => (
        typeof item === 'object'
        && item !== null
        && 'id' in item
        && 'name' in item
        && typeof item.id === 'number'
        && typeof item.name === 'string'
      ))
      .map(item => [String(item.id), { [language]: item.name }]),
  )
}

function emptyCollection<T>() {
  return { items: [] as T[] }
}

async function safeFetch<T>(fetcher: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fetcher()
  }
  catch {
    return fallback
  }
}

async function resolveDdfsFacetLabels<T extends { id: number, name: string }>(
  resource: string,
  facetIds: string[],
  currentLanguage: Language,
): Promise<Record<string, Partial<Record<Language, string>>>> {
  const remainingFacetIds = facetIds.slice(INITIAL_LABEL_LIMIT)
  const response = await safeFetch(
    () => fetchDdfsCollection<T>(resource, { 'id[]': remainingFacetIds }, currentLanguage),
    emptyCollection<T>(),
  )

  return toLabelMap(
    response.items.filter(item => remainingFacetIds.includes(String(item.id))),
    currentLanguage,
  )
}

export async function resolveFacetLabels(
  response: Partial<FacetsResponse>,
  currentLanguage: Language,
  t: TFunction,
  czListSetsByCustomZone: Record<string, { designation: string }> = {},
): Promise<FacetLabels> {
  const productNatureIds = getInitialFacetIds(response.product_natures)
  const brandIds = getInitialFacetIds(response.brands)
  const sportIds = getInitialFacetIds(response.sports)
  const sportGroupIds = getInitialFacetIds(response.sports_groups)
  const countryCodes = getInitialFacetIds(response.production_countries)
  const countriesPromise = countryCodes.length > 0
    ? safeFetch(() => fetchCountriesByCodes(countryCodes, currentLanguage), [] as CountryItem[])
    : Promise.resolve([] as CountryItem[])

  const [productNatures, brands, sports, sportGroups, countries] = await Promise.all([
    productNatureIds.length > 0
      ? safeFetch(() => fetchDdfsCollection<ProductNature>('product_natures', { 'id[]': productNatureIds }, currentLanguage), emptyCollection<ProductNature>())
      : emptyCollection<ProductNature>(),
    brandIds.length > 0
      ? safeFetch(() => fetchDdfsCollection<Brand>('brands', { 'id[]': brandIds }, currentLanguage), emptyCollection<Brand>())
      : emptyCollection<Brand>(),
    sportIds.length > 0
      ? safeFetch(() => fetchDdfsCollection<Sport>('sports', { 'id[]': sportIds }, currentLanguage), emptyCollection<Sport>())
      : emptyCollection<Sport>(),
    sportGroupIds.length > 0
      ? safeFetch(() => fetchDdfsCollection<SportGroup>('sport_groups', { 'id[]': sportGroupIds }, currentLanguage), emptyCollection<SportGroup>())
      : emptyCollection<SportGroup>(),
    countriesPromise,
  ])

  const countryLabels = Object.fromEntries(
    countries
      .filter(country => countryCodes.includes(country.code.toUpperCase()))
      .map(country => [country.code, { [currentLanguage]: country.name }]),
  )

  const designCenterLabels = Object.fromEntries(
    getFacetIds(response.design_centers).map(id => [
      id,
      { [currentLanguage]: getDesignCenterLabel(id, currentLanguage, czListSetsByCustomZone, t) },
    ]),
  )

  return {
    product_natures: toLabelMap(productNatures.items, currentLanguage),
    brands: toLabelMap(brands.items, currentLanguage),
    sports: toLabelMap(sports.items, currentLanguage),
    sports_groups: toLabelMap(sportGroups.items, currentLanguage),
    design_centers: designCenterLabels,
    production_countries: countryLabels,
    selected_customs_zones: Object.fromEntries(
      getFacetIds(response.selected_customs_zones).map(id => [
        id,
        { [currentLanguage]: getCustomsZoneLabel(id, currentLanguage, czListSetsByCustomZone, t) },
      ]),
    ),
  }
}

export async function resolveFacetGroupLabels(
  response: Partial<FacetsResponse>,
  groupId: string,
  currentLanguage: Language,
  t: TFunction,
  czListSetsByCustomZone: Record<string, { designation: string }> = {},
): Promise<FacetLabels> {
  const facetIds = getFacetIds(response[groupId as keyof FacetsResponse] as FacetBucket<string | number>[] | undefined)

  if (facetIds.length <= INITIAL_LABEL_LIMIT)
    return {}

  switch (groupId) {
    case 'product_natures':
      return { product_natures: await resolveDdfsFacetLabels<ProductNature>('product_natures', facetIds, currentLanguage) }
    case 'brands':
      return { brands: await resolveDdfsFacetLabels<Brand>('brands', facetIds, currentLanguage) }
    case 'sports':
      return { sports: await resolveDdfsFacetLabels<Sport>('sports', facetIds, currentLanguage) }
    case 'sports_groups':
      return { sports_groups: await resolveDdfsFacetLabels<SportGroup>('sport_groups', facetIds, currentLanguage) }
    case 'production_countries': {
      const items = await safeFetch(() => fetchAllCountries(currentLanguage), [] as CountryItem[])
      return {
        production_countries: Object.fromEntries(
          items
            .filter(country => facetIds.includes(country.code.toUpperCase()))
            .map(country => [country.code, { [currentLanguage]: country.name }]),
        ),
      }
    }
    case 'design_centers':
      return {
        design_centers: Object.fromEntries(
          facetIds.map(id => [
            id,
            { [currentLanguage]: getDesignCenterLabel(id, currentLanguage, czListSetsByCustomZone, t) },
          ]),
        ),
      }
    case 'selected_customs_zones':
      return {
        selected_customs_zones: Object.fromEntries(
          facetIds.map(id => [
            id,
            { [currentLanguage]: getCustomsZoneLabel(id, currentLanguage, czListSetsByCustomZone, t) },
          ]),
        ),
      }
    default:
      return {}
  }
}
